"use client"

import Image from "next/image";
import {useTranslations} from "next-intl";
import {cn} from "@/lib/utils";
import {getApkUrl} from "@/lib/utils/appLink";
import {AndroidSvg} from "@/app/components/icons/AndroidSvg";
import {ArrowRightSvg} from "@/app/components/icons/ArrowRightSvg";
import {AppQrCode} from "@/app/components/common/app-footer/AppQrCode";
import {useApkDownload} from "@/hooks/use-apk-download";

const SITE = 'aldex.win';

// Breakpoints are container queries on the footer (see AppFooter):
// @min-[640px] = tablet layout (736 in Figma), @min-[1100px] = desktop (1188).
export const AppPromoCard = () => {
    const t = useTranslations();
    const apkUrl = getApkUrl();
    const onDownload = useApkDownload();

    return (
        <section
            aria-labelledby={'footer-app-title'}
            className={cn(
                'relative overflow-hidden rounded-[20px] border border-[#615038]/65',
                'bg-[linear-gradient(110deg,#20201D_0%,#232018_55%,#4C3A1D_100%)]',
                'min-h-[300px] p-[19px] @min-[640px]:min-h-[328px] @min-[640px]:p-[32px]',
            )}
        >
            {/* Gold orbits and glow around the phone, anchored to the right edge like the phone itself.
                On phones they sit off-card in the mockup, so they are not drawn. */}
            <div aria-hidden className={'hidden @min-[640px]:block'}>
                <span className={'absolute right-[-61px] top-[-63px] size-[430px] rounded-full border border-gold-soft/15'}/>
                <span className={'absolute right-[-17px] top-[-20px] size-[342px] rounded-full border border-gold-soft/15'}/>
                <span className={'absolute right-[27px] top-[24px] size-[254px] rounded-full border border-gold-soft/15'}/>
                <span
                    className={'absolute right-[-62px] top-[30px] size-[424px] rounded-full bg-[radial-gradient(closest-side,rgba(227,177,90,0.32),rgba(227,177,90,0))]'}/>
            </div>

            {/* The copy column gives way to the phone (and the QR on desktop) when the footer is
                narrower than the 736 / 1188 mockups, e.g. next to the open desktop sidebar. */}
            <div className={cn('relative z-1 w-[194px] min-w-[160px] max-w-[calc(100%-121px)]',
                '@min-[640px]:w-[410px] @min-[640px]:max-w-[calc(100%-250px)]',
                '@min-[1100px]:w-[420px] @min-[1100px]:max-w-[calc(100%-335px)]')}>
                <p className={'flex items-center gap-[8px] h-[20px] @min-[640px]:h-[24px] text-gold-soft font-semibold uppercase text-[9px] leading-[15px] tracking-[0.6px] @min-[640px]:text-[11px] @min-[640px]:tracking-[0.8px]'}>
                    <AndroidSvg/>
                    {t('AppFooter.appPlatform')}
                </p>

                <h2 id={'footer-app-title'}
                    className={'mt-[8px] font-bold text-text-strong text-[25px] leading-[30px] tracking-[-1.4px] @min-[640px]:text-[36px] @min-[640px]:leading-[42px]'}>
                    <span className={'@min-[640px]:hidden'}>
                        {t('AppFooter.appHeadlineMobile')}<br/>
                        <span className={'text-gold-soft'}>{t('AppFooter.appHeadlineAccentMobile')}</span>
                    </span>
                    <span className={'hidden @min-[640px]:inline'}>
                        {t('AppFooter.appHeadline')}<br/>
                        <span className={'text-gold-soft'}>{t('AppFooter.appHeadlineAccent')}</span>
                    </span>
                </h2>

                <p className={'mt-[8px] text-[#C6C0B5] text-[12px] leading-[18px] @min-[640px]:text-[14px] @min-[640px]:leading-[20px]'}>
                    <span className={'@min-[640px]:hidden'}>{t('AppFooter.appDescriptionMobile')}</span>
                    <span className={'hidden @min-[640px]:inline'}>{t('AppFooter.appDescription')}</span>
                </p>

                <a
                    href={apkUrl || undefined}
                    onClick={apkUrl ? onDownload : undefined}
                    aria-disabled={!apkUrl || undefined}
                    className={cn(
                        'mt-[18px] @min-[640px]:mt-[20px] flex items-center gap-[4px] rounded-[12px] border border-[#B79761] bg-(image:--gradient)',
                        'h-[44px] w-full max-w-[190px] px-[16px] @min-[640px]:max-w-none @min-[640px]:h-[48px] @min-[640px]:w-[254px] @min-[640px]:px-[22px]',
                        'text-noble-black-900 font-semibold text-[14px] leading-[20px] @min-[640px]:text-[15px]',
                        'transition-[filter] hover:brightness-105 active:brightness-95',
                        !apkUrl && 'pointer-events-none opacity-60',
                    )}
                >
                    <AndroidSvg color={'currentColor'} width={22} height={22}/>
                    <span className={'flex-1 text-center'}>{t('AppFooter.downloadApk')}</span>
                    <ArrowRightSvg color={'currentColor'} width={19} height={19} className={'hidden @min-[640px]:block'}/>
                </a>

                <p className={'mt-[8px] text-[#B7AD9B] text-[11px] leading-[16px]'}>
                    <span className={'@min-[640px]:hidden'}>{t('AppFooter.installNoteMobile')}</span>
                    <span className={'hidden @min-[640px]:inline'}>{t('AppFooter.installNote')}</span>
                </p>
                <p className={'mt-[8px] text-[#BFBDB5] text-[11px] leading-[15px]'}>
                    <span className={'@min-[640px]:hidden'}>{t('AppFooter.iphoneGuidanceMobile', {site: SITE})}</span>
                    <span className={'hidden @min-[640px]:inline'}>{t('AppFooter.iphoneGuidance', {site: SITE})}</span>
                </p>
            </div>

            <div className={'absolute right-[276px] top-[220px] z-1 hidden @min-[1100px]:block'}>
                <AppQrCode/>
            </div>

            <PhoneMockup alt={t('AppFooter.mockupAlt')}/>
        </section>
    );
};

// 218×420 at desktop size, scaled down for tablet (0.9) and phone (138 wide) and
// clipped by the card's bottom edge like in the mockup. On cards under 331px it slides
// further off the right edge so the copy column keeps its 160px minimum.
const PhoneMockup = ({alt}: { alt: string }) => (
    <div
        className={cn(
            'absolute origin-top-right right-[min(-6px,calc(100%-325px))] top-[64px] scale-[0.633]',
            '@min-[640px]:right-[24px] @min-[640px]:top-[28px] @min-[640px]:scale-90 @min-[1100px]:scale-100',
            'w-[218px] h-[420px] rounded-[34px] border-2 border-[#82705A] bg-[#111111] p-[7px] shadow-[-14px_16px_32px_rgba(0,0,0,0.55)]',
        )}
    >
        <div className={'h-full overflow-hidden rounded-[27px] bg-[#111111]'}>
            <Image
                src={'/footer/aldex-lobby-android.webp'}
                alt={alt}
                width={506}
                height={922}
                sizes={'204px'}
                loading={'lazy'}
                className={'w-full h-auto'}
            />
        </div>
    </div>
);
